/**
 * Phase 2 MM14 — Sales Package tab (project studio).
 * Generate package from project → sections (edit) → pricing placeholders
 * → review gate + human decision → export internal / watermarked client
 * preview / case-study draft. Honesty: client preview only, concept only,
 * not a contract/invoice/customer approval; human review required.
 */

import { useCallback, useEffect, useState } from "react";
import {
  addSalesDecision, addSalesPricing, createSalesPackage, exportSalesCaseStudy,
  exportSalesClientPreview, exportSalesInternal, getSalesGate, getSalesPackages,
  getSalesPricing, getSalesSections, patchSalesSection,
} from "../api/client";
import { Button } from "../components/Button";
import { StatusPill } from "../components/StatusPill";
import type { StudioStrings } from "../i18n/studioStrings";

type Row = Record<string, unknown>;

export function SalesPackageTab({ projectId, t }:
  { projectId: string; t: StudioStrings }): JSX.Element {
  const [items, setItems] = useState<Row[]>([]);
  const [active, setActive] = useState<Row | null>(null);
  const [section, setSection] = useState("sections");
  const [sections, setSections] = useState<Row[]>([]);
  const [pricing, setPricing] = useState<Row[]>([]);
  const [gate, setGate] = useState<Row | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [priceForm, setPriceForm] = useState({ label: "", amount: "",
    client_visible: true });

  const refresh = useCallback(async () => {
    const r = await getSalesPackages(projectId);
    if (r.outcome === "ok") setItems(r.data?.items ?? []);
    else setNotice(r.status === 403 ? t.permissionDenied : t.loadError);
  }, [projectId, t.permissionDenied, t.loadError]);

  const loadSection = useCallback(async (pkg: Row, sec: string) => {
    setActive(pkg); setSection(sec);
    const id = String(pkg.id);
    if (sec === "sections") {
      const r = await getSalesSections(projectId, id);
      setSections(r.outcome === "ok" ? r.data?.items ?? [] : []);
    } else if (sec === "pricing") {
      const r = await getSalesPricing(projectId, id);
      setPricing(r.outcome === "ok" ? r.data?.items ?? [] : []);
    } else if (sec === "gate") {
      const r = await getSalesGate(projectId, id);
      setGate(r.outcome === "ok" ? (r.data as Row) : null);
    }
  }, [projectId]);

  useEffect(() => { void refresh(); }, [refresh]);

  async function onCreate(): Promise<void> {
    if (title.trim().length < 2) { setNotice(t.loadError); return; }
    const r = await createSalesPackage(projectId, { title, package_type: "proposal" });
    setNotice(r.outcome === "ok" ? null
      : r.status === 403 ? t.permissionDenied : r.errorMessage);
    if (r.outcome === "ok") setTitle("");
    await refresh();
  }

  async function onAddPricing(): Promise<void> {
    if (!active || priceForm.label.trim().length < 1) return;
    await addSalesPricing(projectId, String(active.id), {
      label: priceForm.label, currency: "USD",
      amount: priceForm.amount ? Number(priceForm.amount) : null,
      client_visible: priceForm.client_visible });
    setPriceForm({ label: "", amount: "", client_visible: true });
    await loadSection(active, "pricing");
  }

  async function onDecision(decision: string): Promise<void> {
    if (!active) return;
    const note = decision === "approved_for_client_preview" ? "approved"
      : window.prompt(t.notesLabel) ?? "";
    const r = await addSalesDecision(projectId, String(active.id), decision, note);
    setNotice(r.outcome === "ok" ? `${t.reviewGate}: ${decision}` : r.errorMessage);
    await loadSection(active, "gate"); await refresh();
  }

  async function onExport(kind: "internal" | "client" | "case"): Promise<void> {
    if (!active) return;
    const id = String(active.id);
    const fn = kind === "internal" ? exportSalesInternal
      : kind === "client" ? exportSalesClientPreview : exportSalesCaseStudy;
    const r = await fn(projectId, id);
    setNotice(r.outcome === "ok"
      ? `Exported (${kind}): ${r.data?.artifact_id ?? "—"}` : r.errorMessage);
    await refresh();
  }

  return (
    <section data-testid="project-sales">
      <p className="po-disclaimer" data-testid="sales-honesty">{t.notCustomerApproval}</p>
      {notice ? <p role="alert" className="po-error">{notice}</p> : null}

      <div className="po-row" data-testid="sales-create">
        <input placeholder={t.nameLabel} value={title} data-testid="sales-title"
               onChange={(e) => setTitle(e.target.value)} />
        <Button variant="primary" onClick={() => void onCreate()}>{t.newSalesPackage}</Button>
      </div>

      {items.length === 0 ? <p data-testid="sales-empty">{t.emptyList}</p> : (
        <table className="po-table" data-testid="sales-list">
          <thead><tr><th>{t.nameLabel}</th><th>{t.statusLabel}</th>
            <th>{t.readinessScore}</th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={String(p.id)} style={{ cursor: "pointer" }}
                  onClick={() => void loadSection(p, "sections")}>
                <td>{String(p.title)}</td>
                <td><StatusPill tone={String(p.review_status).startsWith("approved")
                  ? "ok" : "info"} label={String(p.review_status)} /></td>
                <td>{String(p.readiness_score)}%</td>
              </tr>))}
          </tbody>
        </table>)}

      {active ? (
        <div data-testid="sales-detail">
          <h2>{String(active.title)} — {String(active.readiness_score)}%</h2>
          {Array.isArray(active.missing_information) && active.missing_information.length ? (
            <p data-testid="sales-missing">{t.missingFields}:{" "}
              {(active.missing_information as string[]).slice(0, 4).join(" · ")}</p>) : null}
          <nav className="po-tabs">
            {[["sections", t.salesSections], ["pricing", t.pricingBlocks],
              ["gate", t.reviewGate], ["export", t.exportsSection]].map(([k, label]) => (
              <button key={k} type="button" aria-pressed={section === k}
                      className={section === k ? "po-tab po-tab--active" : "po-tab"}
                      onClick={() => void loadSection(active, k)}>{label}</button>))}
          </nav>

          {section === "sections" ? (
            <ul data-testid="sales-sections">
              {sections.map((s) => (
                <li key={String(s.id)}>
                  <strong>{String(s.title)}</strong>{" "}
                  {s.visibility === "internal_only" ? (
                    <StatusPill tone="warn" label="internal only" />) : null}
                  <textarea defaultValue={String(s.body ?? "")}
                            aria-label={String(s.section_type)}
                            onBlur={(e) => void patchSalesSection(projectId,
                              String(active.id), String(s.id), { body: e.target.value })} />
                </li>))}
            </ul>) : null}

          {section === "pricing" ? (
            <div data-testid="sales-pricing">
              <div className="po-row">
                <input placeholder="label" value={priceForm.label}
                       onChange={(e) => setPriceForm({ ...priceForm, label: e.target.value })} />
                <input placeholder="amount (USD)" value={priceForm.amount}
                       onChange={(e) => setPriceForm({ ...priceForm, amount: e.target.value })} />
                <label><input type="checkbox" checked={priceForm.client_visible}
                              onChange={(e) => setPriceForm({ ...priceForm, client_visible: e.target.checked })} />
                  client visible</label>
                <Button variant="secondary" onClick={() => void onAddPricing()}>
                  + {t.pricingBlocks}
                </Button>
              </div>
              <p className="po-disclaimer">manual placeholders — no payment / invoice / FX</p>
              <ul>
                {pricing.map((p) => (
                  <li key={String(p.id)}>{String(p.label)} — {String(p.currency)}{" "}
                    {String(p.amount ?? "—")} · {p.client_visible ? "client" : "internal"}</li>))}
              </ul>
            </div>) : null}

          {section === "gate" ? (
            <div data-testid="sales-gate">
              <p>{t.reviewGate}: <strong>{String(gate?.gate ?? active.review_status)}</strong></p>
              {Array.isArray(gate?.blocking_reasons) && (gate!.blocking_reasons as string[]).length ? (
                <ul>{(gate!.blocking_reasons as string[]).map((r, i) => <li key={i}>🚫 {r}</li>)}</ul>) : null}
              {Array.isArray(gate?.warnings) && (gate!.warnings as string[]).length ? (
                <ul>{(gate!.warnings as string[]).map((r, i) => <li key={i}>⚠ {r}</li>)}</ul>) : null}
              <Button variant="primary" onClick={() => void onDecision("approved_for_client_preview")}>
                approve for client preview
              </Button>{" "}
              <Button variant="secondary" onClick={() => void onDecision("rejected")}>reject</Button>
              <p className="po-disclaimer">{t.notCustomerApproval}</p>
            </div>) : null}

          {section === "export" ? (
            <div data-testid="sales-export">
              <p className="po-disclaimer" data-testid="client-preview-safety">
                {t.clientPreviewSafety}
              </p>
              <Button variant="secondary" onClick={() => void onExport("internal")}>
                {t.exportInternal}
              </Button>{" "}
              <Button variant="secondary" onClick={() => void onExport("client")}>
                {t.exportClientPreview}
              </Button>{" "}
              <Button variant="secondary" onClick={() => void onExport("case")}>
                {t.exportCaseStudy}
              </Button>
            </div>) : null}
        </div>) : null}
    </section>
  );
}
